import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

export default class extends Controller {
  static get values() {
    return { url: String, toggleUrl: String };
  }

  static get targets() {
    return [
      "backdrop", "drawer", "search", "searchInput", "createForm",
      "templatesContainer", "templateList", "perMilestoneToggleCheckbox",
      "allMilestonesTemplateWrapper", "perMilestoneTemplatesWrapper"
    ];
  }

  static get classes() {
    return ["domestic", "international"];
  }

  connect() {
    this.hideTimeoutId = null;
    this.openTimeoutId = null;
  }

  disconnect() {
    clearTimeout(this.hideTimeoutId);
    clearTimeout(this.openTimeoutId);
  }

  showGiftTemplateChooser(event) {
    // Stimulus casts the param to a number, and year 0 (start date) is falsy.
    const milestoneYear = event.params.milestoneYear;
    this.openChooser(event.params.location, milestoneYear === undefined ? "" : String(milestoneYear));
  }

  hideGiftTemplateChooser() {
    this.hideCreateTemplateForm();
    this.milestoneYear = "";

    this.drawerTarget.querySelector(".title-and-actions").classList.remove(this.domesticClass, this.internationalClass);
    this.drawerTarget.classList.remove("open");
    this.templatesContainerTargets.forEach((container) => container.classList.add("hidden"));

    this.hideTimeoutId = setTimeout(() => {
      this.backdropTarget.classList.add("hidden");
      this.drawerTarget.classList.add("hidden");

      document.body.classList.remove("drawer-open");
    }, 200);
  }

  showCreateTemplateForm() {
    this.searchTarget.classList.add("hidden");
    this.templateListTargets.forEach((el) => el.classList.add("hidden"));
    this.createFormTarget.classList.remove("hidden");
  }

  hideCreateTemplateForm() {
    this.searchTarget.classList.remove("hidden");
    this.templateListTargets.forEach((el) => el.classList.remove("hidden"));
    this.createFormTarget.classList.add("hidden");
  }

  useTemplate(event) {
    event.target.disabled = true;
    const templateId = event.currentTarget.dataset.id;

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({
      automation: {
        recipient_location: this.currentLocation,
        gift_template_id: templateId,
        milestone_year: this.milestoneYear
      }
    });

    fetch(this.urlValue, options).
      then((response) => response.json().then((body) => ({ ok: response.ok, status: response.status, body }))).
      then(({ ok, status, body }) => {
        if (ok) {
          const giftTemplateElement = document.querySelector(`.gift-template[data-id="${templateId}"]`);
          const updateTarget = this.updateTargetElement();

          updateTarget.querySelector(".selected-gift-template")?.remove();
          updateTarget.querySelector(".empty-state")?.remove();

          updateTarget.appendChild(this.buildSelectedTemplateMarkup(giftTemplateElement, this.currentLocation, this.milestoneYear));

          event.target.disabled = false;

          if (body.finished) {
            if (!body.editing) {
              window.history.pushState({}, "", "/automations/additional-settings");
            }

            document.querySelector(".additional-settings-wrapper")?.classList.remove("hidden");
          }

          this.hideGiftTemplateChooser();
        } else {
          console.error("Form submission error:", status);
          console.error("Response:", body);
        }
      }).
      catch(this.logFailedCall);
  }

  togglePerMilestone(event) {
    const checked = event.target.checked;

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({ automation: { per_milestone_templates: checked } });

    fetch(this.toggleUrlValue, options).
      then((response) => response.json().then((body) => ({ ok: response.ok, status: response.status, body }))).
      then(({ ok, status, body }) => {
        if (ok) {
          this.syncPerMilestoneCheckboxes(checked);

          if (checked) {
            this.allMilestonesTemplateWrapperTarget.classList.add("hidden");
            this.perMilestoneTemplatesWrapperTarget.classList.remove("hidden");
          } else {
            this.perMilestoneTemplatesWrapperTarget.classList.add("hidden");
            this.allMilestonesTemplateWrapperTarget.classList.remove("hidden");
          }

          const additionalSettingsWrapper = document.querySelector(".additional-settings-wrapper");

          if (body.finished) {
            additionalSettingsWrapper?.classList.remove("hidden");
          } else {
            additionalSettingsWrapper?.classList.add("hidden");
            window.history.pushState({}, "", "/automations/gift-templates");
          }

          // Re-evaluate horizontal scroll after the DOM updates
          requestAnimationFrame(() => {
            const container = document.querySelector(".automations-container");
            if (!container) { return; }

            if (container.scrollWidth > container.clientWidth) {
              container.classList.add("enable-scrolling");
            } else {
              container.classList.remove("enable-scrolling");
            }
          });
        } else {
          console.error("Toggle error:", status);
          // Revert the checkbox if the request failed
          event.target.checked = !event.target.checked;
        }
      }).
      catch(this.logFailedCall);
  }

  // Templates Search Code
  filterTemplates(event) {
    const searchTerm = event.target.value.trim().toLowerCase();
    const visibleContainer = this.templatesContainerTargets.find((container) => !container.classList.contains("hidden"));
    const templates = visibleContainer ? visibleContainer.querySelectorAll(".gift-template") : [];

    if (!searchTerm) {
      templates.forEach((template) => template.classList.remove("hidden"));
      return;
    }

    templates.forEach((template) => {
      const templateName = template.dataset.name.toLowerCase();
      const templateBudget = template.dataset.budget;

      if (templateName.includes(searchTerm) || templateBudget.includes(searchTerm) || `$${templateBudget}`.includes(searchTerm)) {
        template.classList.remove("hidden");
      } else {
        template.classList.add("hidden");
      }
    });
  }

  resetTemplateVisibility() {
    this.drawerTarget.querySelectorAll(".gift-template").forEach((template) => template.classList.remove("hidden"));
  }

  validateName(event) {
    const name = event.currentTarget.value;
    const submitButton = event.currentTarget.closest("form").querySelector("input[type='submit'], button[type='submit']");

    submitButton.disabled = name.length === 0;
  }

  openChooser(location, milestoneYear = "") {
    this.currentLocation = location;
    this.milestoneYear = milestoneYear;
    this.searchInputTarget.value = "";

    // #gift_template_location lives in a partial shared with the non-automation gift-templates
    // controller (subdomain/templates/gifts/_form.html.haml) -- it can't carry this controller's
    // target attribute, so it stays a plain lookup.
    const locationField = this.element.querySelector("#gift_template_location");
    if (locationField) { locationField.value = location; }

    this.backdropTarget.classList.remove("hidden");

    const titleAndActions = this.drawerTarget.querySelector(".title-and-actions");
    titleAndActions.classList.add(location === "domestic" ? this.domesticClass : this.internationalClass);
    titleAndActions.querySelector(".title").textContent = this.modalTitle(location);

    this.drawerTarget.querySelector(`.templates-container.${location}-templates`).classList.remove("hidden");
    this.drawerTarget.classList.remove("hidden");

    this.resetTemplateVisibility();

    this.openTimeoutId = setTimeout(() => this.drawerTarget.classList.add("open"), 5);

    document.body.classList.add("drawer-open");
  }

  modalTitle(location) {
    if (location === "domestic") {
      return "Select a 🇺🇸 Domestic (U.S.) Gift Template.";
    } else {
      return "Select an 🌎 International Gift Template.";
    }
  }

  updateTargetElement() {
    const locationClass = `.${this.currentLocation}-recipients`;

    if (this.milestoneYear) {
      return this.element.querySelector(`${locationClass}[data-year="${this.milestoneYear}"]`);
    }

    // When no milestone year, scope to the all-milestones wrapper to avoid
    // matching per-milestone slots with the same location class. Only the
    // work anniversary form renders that wrapper.
    const scope = this.hasAllMilestonesTemplateWrapperTarget ? this.allMilestonesTemplateWrapperTarget : this.element;

    return scope.querySelector(locationClass);
  }

  buildSelectedTemplateMarkup(giftTemplateElement, location, milestoneYear) {
    const image = giftTemplateElement.querySelector(".gift-template-design img");
    const name = giftTemplateElement.dataset.name;
    const collectionInformation = giftTemplateElement.dataset.collectionInformation;

    const node = RequestHelper.htmlNodeFromString(`<div class="selected-gift-template">
        <div class="image">
          ${image ? `<img src="${image.getAttribute("src")}">` : ""}
        </div>
        <div class="details">
          <div class="name">${name}</div>
          <div class="collection">${collectionInformation}</div>
        </div>
      </div>`);

    node.addEventListener("click", () => this.openChooser(location, milestoneYear));

    return node;
  }

  syncPerMilestoneCheckboxes(checked) {
    this.perMilestoneToggleCheckboxTargets.forEach((checkbox) => { checkbox.checked = checked; });
  }

  logFailedCall(response) { console.warn(response); }
}
